import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { notificationManager } from '../utils/notificationManager';
import { PackagesResponse, ErrorHandlerPackagesResponse } from '../types';

// User search result interface for admin wallet functionality
interface UserSearchResult {
  id: string;
  email: string;
  name: string;
  role: string;
  balance: number;
}

// API Base URL configuration with fallback for better reliability
const getApiBaseUrl = () => {
  const normalizeToApiBase = (raw?: string) => {
    if (!raw) return null;
    let trimmed = raw.trim().replace(/\s+/g, '');
    if (!trimmed) return null;

    if (
      process.env.NODE_ENV === 'production' &&
      typeof window !== 'undefined' &&
      window.location?.protocol === 'https:' &&
      trimmed.startsWith('http://') &&
      !trimmed.includes('localhost') &&
      !trimmed.includes('127.0.0.1')
    ) {
      trimmed = `https://${trimmed.slice('http://'.length)}`;
    }

    // Ensure trailing '/api' once
    if (/\/api\/?$/.test(trimmed)) {
      return trimmed.replace(/\/$/, '');
    }
    if (trimmed.endsWith('/')) return `${trimmed}api`;
    return `${trimmed}/api`;
  };

  // In production, prefer explicit env var (Vercel) but fall back to proxy path
  if (process.env.NODE_ENV === 'production') {
    return '/api';
  }
 
  // Try multiple endpoints in order of preference for development
  const endpoints = [
    normalizeToApiBase(process.env.REACT_APP_API_URL) || undefined,
    'https://ghdata-market-api.onrender.com/api',
    'http://localhost:5000/api' // Fallback for development
  ].filter(Boolean);

  return endpoints[0] || endpoints[1];
};

const API_BASE_URL = getApiBaseUrl();

let hadConnectionError = false;
let lastConnectionErrorShownAt = 0;
const CONNECTION_ERROR_COOLDOWN_MS = 30000;

// Token refresh mutex to prevent concurrent refresh requests
let isRefreshing = false;
let refreshSubscribers: Array<(success: boolean) => void> = [];

const subscribeToRefresh = (callback: (success: boolean) => void) => {
  refreshSubscribers.push(callback);
};

const onRefreshComplete = (success: boolean) => {
  refreshSubscribers.forEach(callback => callback(success));
  refreshSubscribers = [];
};

// Enhanced retry configuration for better reliability
const MAX_RETRIES = 3; // Increased for better reliability
const RETRY_DELAY_BASE = 1000; // 1 second base delay
const RETRYABLE_STATUS_CODES = [408, 500, 502, 503, 504]; // Include 500 for retry
const RETRYABLE_ERRORS = ['NETWORK_ERROR', 'TIMEOUT', 'ERR_CONNECTION_CLOSED', 'ERR_CONNECTION_REFUSED'];
const NON_RETRYABLE_STATUS_CODES = [401, 403, 404, 422, 429]; // Reduced non-retryable codes

// Create axios instance with enhanced configuration
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 45000, // Increased timeout for better reliability
  headers: {
    'Content-Type': 'application/json'
    // Removed User-Agent header to avoid browser security warnings
  },
  withCredentials: true
});

const getCookieValue = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  const raw = document.cookie;
  if (!raw) return null;
  const parts = raw.split(';').map((p) => p.trim());
  const prefix = `${name}=`;
  const found = parts.find((p) => p.startsWith(prefix));
  if (!found) return null;
  return decodeURIComponent(found.slice(prefix.length));
};

// Extend the config type to include metadata
declare module 'axios' {
  interface InternalAxiosRequestConfig {
    metadata?: {
      retryCount?: number;
      isBackground?: boolean;
      maxRetries?: number;
      requestTime?: string;
      url?: string;
      method?: string;
      hasAuth?: boolean; // Added to track authentication state
    };
  }
}

// Enhanced request interceptor with comprehensive auth handling
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    try {
      // Track request metadata
      config.metadata = {
        retryCount: config.metadata?.retryCount || 0,
        isBackground: config.metadata?.isBackground || false,
        maxRetries: config.metadata?.maxRetries || MAX_RETRIES,
        requestTime: new Date().toISOString(),
        url: config.url,
        method: config.method,
        hasAuth: false
      };

      // If we're sending FormData, let the browser set the correct multipart boundary
      if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
        if (config.headers) {
          delete (config.headers as any)['Content-Type'];
        }
      }

      const method = String(config.method || 'get').toLowerCase();
      const isStateChanging = !['get', 'head', 'options'].includes(method);
      if (isStateChanging) {
        const csrfToken = getCookieValue('csrfToken');
        if (csrfToken) {
          config.headers = config.headers || {};
          (config.headers as any)['X-CSRF-Token'] = csrfToken;
        }
      }
      
      // Add request metadata for debugging
      config.metadata = {
        ...config.metadata,
        requestTime: new Date().toISOString(),
        url: config.url,
        method: config.method?.toUpperCase(),
        hasAuth: Boolean(getCookieValue('csrfToken'))
      };
      
    } catch (error) {
      globalThis.console.error('Request interceptor failed');
      // Don't throw here, let the request proceed and handle errors in response interceptor
    }
    
    return config;
  },
  (error) => {
    globalThis.console.error('Request interceptor failed');
    return Promise.reject(error);
  }
);

// Enhanced response interceptor with comprehensive error handling
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      const url = String(originalRequest?.url || '');
      const isAuthEndpoint = url.includes('/auth/login')
        || url.includes('/auth/register')
        || url.includes('/auth/refresh')
        || url.includes('/auth/logout')
        || url.includes('/auth/exchange');

      if (!isAuthEndpoint) {
        originalRequest._retry = true;
        
        // If already refreshing, wait for the refresh to complete
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            subscribeToRefresh((success: boolean) => {
              if (success) {
                resolve(api(originalRequest));
              } else {
                reject(error);
              }
            });
          });
        }
        
        // Start refresh
        isRefreshing = true;
        
        try {
          await api.post('/auth/refresh');
          isRefreshing = false;
          onRefreshComplete(true);
          return api(originalRequest);
        } catch (refreshError) {
          isRefreshing = false;
          onRefreshComplete(false);
          return Promise.reject(refreshError);
        }
      }
    }
    
    return Promise.reject(error);
  }
);

// Flag to prevent redirect during initial token validation
let isInitialValidation = false;

export const setInitialValidation = (value: boolean) => {
  isInitialValidation = value;
};

// Calculate exponential backoff delay
const getRetryDelay = (retryCount: number): number => {
  return Math.min(RETRY_DELAY_BASE * Math.pow(2, retryCount), 10000); // Max 10 seconds
};

// Check if request should be retried
const shouldRetry = (error: any, retryCount: number): boolean => {
  if (retryCount >= MAX_RETRIES) return false;
  
  // NEVER retry on authentication, authorization, validation, or not found errors
  if (error.response?.status && NON_RETRYABLE_STATUS_CODES.includes(error.response.status)) {
    return false;
  }
  
  // NEVER retry admin dashboard stats - prevents boot blocking
  if (error.config?.url?.includes('/admin/dashboard/stats')) {
    globalThis.console.warn('Admin dashboard temporarily unavailable');
    return false;
  }
  
  // Check for retryable status codes
  if (error.response?.status && RETRYABLE_STATUS_CODES.includes(error.response.status)) {
    return true;
  }
  
  // Check for retryable error codes or messages
  const errorCode = error.code || error.message?.toUpperCase();
  if (errorCode && RETRYABLE_ERRORS.some(retryableError => 
    errorCode.includes(retryableError) || error.message?.includes(retryableError.toLowerCase())
  )) {
    return true;
  }
  
  return false;
};

// Enhanced response interceptor with retry logic
api.interceptors.response.use(
  (response: AxiosResponse) => {
    hadConnectionError = false;
    lastConnectionErrorShownAt = 0;
    return response;
  },
  async (error) => {
    const config = error.config;
    
    // Initialize retry count if not present
    if (!config.metadata) {
      config.metadata = { retryCount: 0 };
    }
    
    const retryCount = config.metadata.retryCount || 0;
    
    // Check if we should retry the request
    if (shouldRetry(error, retryCount)) {
      config.metadata.retryCount = retryCount + 1;
      
      // Calculate delay for exponential backoff
      const delay = getRetryDelay(retryCount);
      
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delay));
      
      // Retry the request
      return api(config);
    }
    
    // Handle different error types - CONSOLE HYGIENE
    if (error.code === 'NO_AUTH_TOKEN') {
      // Silently handle missing auth token - no console noise
      return Promise.reject(error);
    } else if (error.code === 'ECONNREFUSED' || error.message?.includes('Network Error')) {
      if (!config.metadata?.isBackground) {
        const now = Date.now();
        const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false;

        if (!hadConnectionError || now - lastConnectionErrorShownAt > CONNECTION_ERROR_COOLDOWN_MS) {
          hadConnectionError = true;
          lastConnectionErrorShownAt = now;

          notificationManager.error(
            isOffline
              ? 'You appear to be offline. Please check your internet connection.'
              : 'Unable to reach the server right now. Please try again.',
            {
              id: 'connection-error'
            }
          );
        }
      }
    } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      notificationManager.error('Request timed out. Please try again.', {
        id: 'timeout-error'
      });
    } else if (error.response) {
      const status = error.response.status;
      const data = error.response.data;
      
      switch (status) {
        case 401:
          // SILENTLY handle 401 errors - no console noise
          if (!isInitialValidation && !error.config._retry) {
            // Only show user notification for manual actions, not automatic calls
            if (!error.config.metadata?.isBackground) {
              notificationManager.error('Your session has expired. Please log in again.', {
                id: 'session-expired'
              });
            }
          }
          break;
        case 403:
          // Phone verification required — redirect to verify page
          if (error.response?.data?.requiresPhoneVerification) {
            window.location.href = '/verify-phone';
            return Promise.reject(error);
          }
          // Don't retry on permission errors.
          // If this is a feature-flag block, show a user-friendly info/warning instead.
          if (
            data?.code === 'PURCHASE_DISABLED' ||
            data?.code === 'DEPOSIT_DISABLED' ||
            data?.code === 'DIRECT_PURCHASE_DISABLED' ||
            data?.code === 'MANUAL_TOPUP_DISABLED'
          ) {
            const featureDisabledMessages: Record<string, string> = {
              PURCHASE_DISABLED: 'Purchases are currently unavailable. Please try again later.',
              DEPOSIT_DISABLED: 'Deposits are currently unavailable. Please try again later.',
              DIRECT_PURCHASE_DISABLED: 'Direct purchase is currently unavailable. Please use your wallet balance.',
              MANUAL_TOPUP_DISABLED: 'Manual top-up is currently unavailable. Please contact support if you need help.'
            };

            notificationManager.warning(
              featureDisabledMessages[String(data?.code)] || data?.message || 'This feature is currently unavailable. Please try again later.',
              {
                id: `feature-disabled-${data?.code || 'unknown'}`,
                icon: null
              }
            );
          } else {
            const url = String(error?.config?.url || '');
            const method = String(error?.config?.method || '').toLowerCase();
            const suppressForOrderFlows =
              !url.startsWith('/admin') &&
              (
                (url.startsWith('/orders') && (method === 'post' || method === 'get')) ||
                (url.startsWith('/bulk-orders') && (method === 'post' || method === 'get'))
              );

            if (suppressForOrderFlows) {
              break;
            }

            notificationManager.error('You do not have permission to access this resource.', {
              id: 'permission-error'
            });
          }
          break;
        case 404:
          // Don't retry on not found errors
          notificationManager.error('The requested resource was not found.', {
            id: 'not-found-error'
          });
          break;
        case 422:
          // Don't retry on validation errors
          const validationMessage = data?.message || 'Invalid data provided';
          notificationManager.error(validationMessage, {
            id: 'validation-error'
          });
          break;
        case 429:
          // Rate limit - let the caller decide how to surface this (avoid global toast spam)
          break;
        default:
          // For other server errors, don't show generic errors that might confuse users
          // Let the component handle specific error states
          break;
      }
    } else if (!config.metadata?.isBackground) {
      notificationManager.error('An unexpected error occurred. Please try again.');
    }
    
    return Promise.reject(error);
  }
);

// Helper function to make background requests (no notifications on error)
export const makeBackgroundRequest = async (config: any) => {
  const backgroundConfig = {
    ...config,
    metadata: { ...config.metadata, isBackground: true }
  };
  return api(backgroundConfig);
};

// Helper function to make requests with custom retry configuration
export const makeRequestWithRetry = async (config: any, maxRetries: number = MAX_RETRIES) => {
  const retryConfig = {
    ...config,
    metadata: { ...config.metadata, maxRetries }
  };
  return api(retryConfig);
};

export default api;

// Types - Updated to match backend API structure
export interface LoginResponse {
  success: boolean;
  message: string;
  data: {
    user: any;
  };
}

export interface RegisterResponse {
  success: boolean;
  message: string;
  approvalMessage?: string;
  paymentInfo?: {
    required: boolean;
    amount: number;
    currency: string;
    description: string;
    status: string;
  };
  data: {
    user: any;
  };
}

export interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  data: T;
}

export interface PaginatedResponse<T = any> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

// Auth API
export const authAPI = {
  login: (identifier: string, password: string, rememberMe: boolean = false) =>
    api.post<LoginResponse>('/auth/login', { identifier, password, rememberMe }),
  
  register: (userData: any) =>
    api.post<RegisterResponse>('/auth/register', userData),
  
  getMe: () => api.get<ApiResponse<{ user: any; navigation?: any[]; features?: string[]; permissions?: any }>>('/auth/me'),
  
  updateProfile: (profileData: any) =>
    api.put<ApiResponse<{ user: any }>>('/auth/profile', profileData),
  
  updateContact: (contactData: { email?: string; phone?: string }) =>
    api.put<ApiResponse<{ user: any }>>('/auth/contact', contactData),
  
  changePassword: (currentPassword: string, newPassword: string) =>
    api.put<ApiResponse>('/auth/password', { currentPassword, newPassword }),
  
  updateSettings: (settings: any) =>
    api.put<ApiResponse>('/auth/settings', settings),
  
  forgotPassword: (email: string) =>
    api.post<ApiResponse>('/auth/forgot-password', { email }),

  resetPassword: (token: string, password: string) =>
    api.post<ApiResponse>('/auth/reset-password', { token, password }),

  refreshToken: () => api.post<ApiResponse>('/auth/refresh'),

  logout: () => api.post<ApiResponse>('/auth/logout'),

  exchangeAuthCode: (code: string) =>
    api.post<LoginResponse>('/auth/exchange', { code }),
};

export const twoFactorAPI = {
  generateSecret: () =>
    api.post('/2fa/generate'),
  verifyToken: (token: string) =>
    api.post('/2fa/verify', { token }),
  disable2FA: (token: string) =>
    api.post('/2fa/disable', { token }),
  getStatus: () =>
    api.get('/2fa/status'),
  generateBackupCodes: (token: string) =>
    api.post('/2fa/backup-codes', { token }),
};

// Packages API
export const packagesAPI = {
  getPackages: (params?: any) =>
    api.get<PackagesResponse | ErrorHandlerPackagesResponse>('/packages', {
      params: {
        limit: 50,
        ...(params || {})
      }
    }),
  
  getPackage: (id: string, includeDiscounts?: boolean) =>
    api.get<{ package: any }>(`/packages/${id}`, {
      params: { includeDiscounts: includeDiscounts ? 'true' : 'false' }
    }),
  
  getNetworkPackages: (network: string, params?: any) =>
    api.get<PackagesResponse>(`/packages/network/${network}`, {
      params: {
        limit: 50,
        ...(params || {})
      }
    }),
  
  getPopularPackages: (limit?: number, includeDiscounts?: boolean) =>
    api.get<ApiResponse<{ packages: any[] }>>('/packages/popular/list', {
      params: { limit, includeDiscounts: includeDiscounts ? 'true' : 'false' },
    }),
  
  searchPackages: (query: string, params?: any) =>
    api.get<PackagesResponse>('/packages/search/query', {
      params: { q: query, ...params },
    }),
};

// Admin Packages API
export const adminPackagesAPI = {
  getPackages: async (params?: any) => {
    const maxRetries = 3;
    let retryCount = 0;
    
    const attempt = async (): Promise<any> => {
      try {
        globalThis.console.log(`[AdminPackagesAPI] Fetching packages (attempt ${retryCount + 1}/${maxRetries})`, params);
        
        const response = await api.get('/admin/packages', { 
          params: {
            limit: 100,
            includeInactive: 'true',
            ...params
          },
          timeout: 15000 // Longer timeout for admin operations
        });
        
        // Validate response structure
        if (!response?.data) {
          throw new Error('Invalid response structure from server');
        }
        
        const responseData = response.data as any;
        let packagesData = [];
        let paginationData = {
          page: 1,
          limit: 100,
          total: 0,
          pages: 0
        };
        let summaryData = {
          total: 0,
          active: 0,
          inactive: 0,
          popular: 0,
          byNetwork: {},
          byCategory: {}
        };
        
        // Handle different response structures
        if (responseData.data && responseData.data.packages) {
          packagesData = responseData.data.packages;
          paginationData = responseData.data.pagination || paginationData;
          summaryData = responseData.data.summary || summaryData;
        } else if (responseData.packages && Array.isArray(responseData.packages)) {
          packagesData = responseData.packages;
          paginationData = responseData.pagination || paginationData;
          summaryData = responseData.summary || summaryData;
        } else if (Array.isArray(responseData)) {
          packagesData = responseData;
        }
        
        globalThis.console.log(`[AdminPackagesAPI] Successfully fetched ${packagesData.length} packages`);
        globalThis.console.log(`[AdminPackagesAPI] Package summary:`, summaryData);
        
        return {
          data: {
            data: packagesData,
            packages: packagesData,
            pagination: paginationData,
            summary: summaryData
          }
        };
        
      } catch (error: any) {
        globalThis.console.error(`[AdminPackagesAPI] Packages fetch failed (attempt ${retryCount + 1}):`, error);
        
        // Retry logic for network errors
        if (retryCount < maxRetries - 1 && 
            (error.code === 'NETWORK_ERROR' || 
             error.message?.includes('Network Error') || 
             error.response?.status >= 500)) {
          retryCount++;
          const delay = Math.min(1000 * Math.pow(2, retryCount), 5000); // Exponential backoff
          globalThis.console.log(`[AdminPackagesAPI] Retrying in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
          return attempt();
        }
        
        // Return fallback data on final failure
        globalThis.console.log('[AdminPackagesAPI] Using fallback data');
        return {
          data: {
            data: [],
            packages: [],
            pagination: {
              page: 1,
              limit: 100,
              total: 0,
              pages: 0
            },
            summary: {
              total: 0,
              active: 0,
              inactive: 0,
              popular: 0,
              byNetwork: {},
              byCategory: {}
            }
          }
        };
      }
    };
    
    return attempt();
  },
  
  getPackage: (id: string) =>
    api.get<ApiResponse<{ package: any }>>(`/admin/packages/${id}`),
  
  createPackage: (packageData: any) =>
    api.post<ApiResponse<{ package: any }>>('/admin/packages', packageData),
  
  updatePackage: (id: string, packageData: any) =>
    api.put<ApiResponse<{ package: any }>>(`/admin/packages/${id}`, packageData),
  
  deletePackage: (id: string) =>
    api.delete<ApiResponse>(`/admin/packages/${id}`),
  
  togglePopular: (id: string) =>
    api.patch<ApiResponse<{ package: any }>>(`/admin/packages/${id}/toggle-popular`),
};

// Discounts API
export const discountsAPI = {
  getDiscounts: (params?: any) =>
    api.get<ApiResponse<{ discounts: any[]; pagination: any }>>('/discounts', { params }),
  
  getApplicableDiscounts: (params?: any) =>
    api.get<ApiResponse<{ discounts: any[] }>>('/discounts/applicable', { params }),

  validateCoupon: (payload: { code: string; packageId?: string }) =>
    api.post<{
      success: boolean;
      message?: string;
      code?: string;
      coupon?: {
        code: string;
        name: string;
        type: 'percentage' | 'fixed';
        value: number;
        originalPrice: number;
        discountedPrice: number;
        totalDiscount: number;
      };
    }>('/discounts/validate', payload),
  
  createDiscount: (discountData: any) =>
    api.post<ApiResponse<{ discount: any }>>('/discounts', discountData),
  
  updateDiscount: (id: string, discountData: any) =>
    api.put<ApiResponse<{ discount: any }>>(`/discounts/${id}`, discountData),
  
  deleteDiscount: (id: string) =>
    api.delete<ApiResponse>(`/discounts/${id}`),
  
  toggleDiscount: (id: string) =>
    api.patch<ApiResponse<{ discount: any }>>(`/discounts/${id}/toggle`),
  
  getStats: () =>
    api.get<ApiResponse<any>>('/discounts/stats/overview'),
};

// Orders API
export const ordersAPI = {
  createOrder: (orderData: any) =>
    api.post<ApiResponse<{ order: any }>>('/orders', orderData),

  initializePaystackOrder: (orderData: any) =>
    api.post<ApiResponse<{ reference: string; access_code: string; authorization_url: string; amount: number; currency: string }>>(
      '/orders/paystack/initialize',
      orderData
    ),

  createMobileMoneyOrder: (orderData: any) =>
    api.post<ApiResponse<{ order: any; orderId: string; amount: number }>>('/orders/mobile-money/create', orderData),
  
  getMyOrders: (params?: any) =>
    api.get<PaginatedResponse>('/orders/my-orders', { params }),
  
  getOrder: (id: string) =>
    api.get<ApiResponse<{ order: any }>>(`/orders/${id}`),

  trackOrder: (id: string) =>
    api.get<ApiResponse<{ tracking: any }>>(`/orders/${id}/track`),

  getLiveFeed: () =>
    api.get<
      ApiResponse<{
        lastDelivered: any | null;
        activeOrder: any | null;
        activeOrders: any[];
      }>
    >('/orders/live-feed'),
  
  getOrders: (params?: any) =>
    api.get<PaginatedResponse>('/orders', { params }),
  
  updateOrderStatus: (id: string, status: string, adminNotes?: string) => {
    const normalizedStatus = String(status || '').toLowerCase().trim();
    const confirmDelivery = normalizedStatus === 'completed' || normalizedStatus === 'delivered' || normalizedStatus === 'successful' || normalizedStatus === 'success';

    return api.patch<ApiResponse<{ order: any }>>(`/orders/${id}/status`, {
      status,
      adminNotes,
      ...(confirmDelivery ? { confirmDelivery: true } : {})
    });
  },

  verifyOrderPayment: (id: string, notes?: string, providerReference?: string) =>
    api.post<ApiResponse<{ order: any }>>(`/orders/${id}/verify-payment`, {
      notes,
      providerReference
    }),
  
  refundOrder: (id: string, amount: number, reason: string) =>
    api.post<ApiResponse<{ order: any }>>(`/orders/${id}/refund`, {
      amount,
      reason,
    }),

  requestRefund: (id: string) =>
    api.post<ApiResponse<{ order: any; amount: number }>>(`/orders/${id}/refund-request`, {}),
  
  getOrderStats: (params?: any) =>
    api.get<ApiResponse<{ stats: any[]; period: any }>>('/orders/stats/summary', {
      params,
    }),

  getMySalesDashboard: () =>
    api.get<
      ApiResponse<{
        totals: {
          totalOrders: number;
          totalSpent: number;
        };
        thisWeek: {
          orders: number;
          spent: number;
          startDate: string;
          endDate: string;
          dailySpent: Array<{ day: string; spent: number; orders: number }>;
        };
      }>
    >('/orders/my-sales-dashboard'),

  getMyOrderBreakdown: (params?: { period?: 'all' | 'week' | 'month' }) =>
    api.get<
      ApiResponse<{
        byNetwork: Record<string, number>;
        byStatus: Record<string, number>;
        normalized: {
          accepted: number;
          failed: number;
          processing: number;
          delivered: number;
        };
        period: string;
      }>
    >('/orders/my-order-breakdown', { params }),

  /**
   * Pre-purchase advisory check: asks the backend whether the given recipient
   * phone number has a prior completed DATA_PURCHASE order.
   *
   * Response shape (from backend):
   *   { success: true, applicable: false }                           ← non-MTN number, check not relevant
   *   { success: true, applicable: true, known: true,  message: '' } ← MTN, previously seen
   *   { success: true, applicable: true, known: false, message: '' } ← MTN, first-time
   *
   * @param phone Raw phone number (any Ghanaian format)
   * @param confirmedNetwork Optional — 'MTN' | 'Telecel' | 'AirtelTigo'.
   *   Pass when the user confirmed the number is ported; overrides prefix inference.
   */
  checkNumber: (phone: string, confirmedNetwork?: string) => {
    const params: Record<string, string> = { phone };
    if (confirmedNetwork) params.confirmedNetwork = confirmedNetwork;
    return api.get<any>('/orders/check-number', { params });
  },
};

// Payments API
export const paymentsAPI = {
  initializePayment: (paymentData: any) =>
    api.post<ApiResponse<{ payment: any; authorization_url: string; access_code: string; reference: string; amount: number; currency: string }>>(
      '/payments/initialize',
      paymentData
    ),
  
  verifyPayment: (reference: string) =>
    api.post<ApiResponse<{ payment: any; order: any }>>('/payments/verify', {
      reference,
    }),

  getMobileMoneyOrderStatus: (reference: string) =>
    api.get<ApiResponse<{ orderId: string; reference: string; paymentReference: string | null; paymentStatus: string; paymentType?: string | null; status: string; updatedAt: string }>>(
      `/payments/mobile-money/order-status/${encodeURIComponent(String(reference || '').trim())}`
    ),
  
  createMobileMoneyPayment: (paymentData: any) =>
    api.post<ApiResponse<{ payment: any; order: any }>>('/payments/mobile-money', paymentData),
  
  getMyPayments: (params?: any) =>
    api.get<PaginatedResponse>('/payments/my-payments', { params }),
  
  getPayments: (params?: any) =>
    api.get<PaginatedResponse>('/payments', { params }),
  
  approvePayment: (id: string, adminNotes?: string) =>
    api.patch<ApiResponse<{ payment: any; order: any }>>(`/payments/${id}/approve`, {
      adminNotes,
    }),
  
  rejectPayment: (id: string, reason: string) =>
    api.patch<ApiResponse<{ payment: any; order: any }>>(`/payments/${id}/reject`, {
      reason,
    }),
  
  getPaymentStats: (params?: any) =>
    api.get<ApiResponse<{ stats: any[]; period: any }>>('/payments/stats/summary', {
      params,
    }),

  getAllPayments: (params?: any) =>
    api.get<any>('/payments', { params }),

  exportPayments: (filters?: any) =>
    api.get('/payments/export', { 
      params: filters,
      responseType: 'blob',
    }),
};

// Wallet API
export const walletAPI = {
  getBalance: () =>
    api.get<ApiResponse<{ wallet: any }>>('/wallets/balance'),
  
  getTransactions: (params?: any) =>
    api.get<PaginatedResponse>('/wallets/transactions', { params }),
  
  topupWallet: (topupData: any) =>
    api.post<ApiResponse<{ transaction: any }>>('/wallets/topup', topupData),
  
  completeTopup: (transactionId: string, paymentReference: string) =>
    api.post<ApiResponse<{ transaction: any; wallet: any }>>('/wallets/topup/complete', {
      transactionId,
      paymentReference,
    }),
  
  getWalletStats: (params?: any) =>
    api.get<ApiResponse<{ stats: any; period: any; currentBalance: number }>>('/wallets/stats', {
      params,
    }),
  
  // Admin wallet functions
  getAdminTransactions: (params?: any) =>
    api.get<PaginatedResponse>('/wallets/admin/transactions', { params }),

  getPendingMomoTopups: (params?: any) =>
    api.get<ApiResponse<{ requests: any[]; pagination: any }>>('/wallets/admin/momo/pending', { params }),

  approveMomoTopup: (data: { reference: string; externalReference?: string; notes?: string }) =>
    api.post<ApiResponse<any>>('/wallets/admin/momo/approve', data),

  rejectMomoTopup: (data: { reference: string; reason: string }) =>
    api.post<ApiResponse<any>>('/wallets/admin/momo/reject', data),

  checkDepositStatus: (reference: string) =>
    api.get<ApiResponse<any>>(`/wallets/deposit/status/${encodeURIComponent(reference)}`),
  
  adjustWallet: (adjustmentData: any) =>
    api.post<ApiResponse<{ transaction: any; userWallet: any }>>('/wallets/admin/adjust', adjustmentData),
  
  getUserWallet: (userId: string) =>
    api.get<ApiResponse<{ user: any }>>(`/wallets/${userId}`),
};

// Users API
export const usersAPI = {
  getAllUsers: (params?: any) =>
    api.get<any>('/users', { params }),

  getUsers: (params?: any) =>
    api.get<{ users: any[], pagination: any }>('/users', { params }),

  createUser: (userData: any) =>
    api.post<{ message: string; user: any }>('/users', userData),
  
  getUser: (id: string) =>
    api.get<{ user: any }>(`/users/${id}`),
  
  updateUser: (id: string, userData: any) =>
    api.put<{ user: any }>(`/users/${id}`, userData),
  
  deleteUser: (id: string) =>
    api.delete<any>(`/users/${id}`),
  
  toggleUserStatus: (id: string) =>
    api.patch<{ user: any }>(`/users/${id}/toggle-status`),
  
  verifyUser: (id: string) =>
    api.patch<{ user: any }>(`/users/${id}/verify`),
  
  // Enhanced User Management APIs
  resetUserPassword: (id: string, newPassword: string) =>
    api.patch<any>(`/users/${id}/reset-password`, { newPassword }),
  
  updateUserNotifications: (id: string, enabled: boolean) =>
    api.patch<any>(`/users/${id}/notifications`, { notificationsEnabled: enabled }),
  
  exportUsers: () =>
    api.get('/users/export', { responseType: 'blob' }),
  
  exportUsersDetails: () =>
    api.get<any[]>('/users/export-details'),
    
  exportRecipientsDetails: () =>
    api.get<any[]>('/orders/export-recipients'),
  
  bulkUserAction: (action: string, userIds: string[]) =>
    api.post<any>('/users/bulk-action', { action, userIds }),
  
  updateRole: (id: string, role: string) =>
    api.patch<{ user: any }>(`/users/${id}/role`, { role }),
  
  getUserStats: () =>
    api.get<{ stats: any; recentUsers: any[] }>('/users/stats/summary'),
  
  getUserWallet: (id: string) =>
    api.get<{ user: any }>(`/users/${id}/wallet`),
};

// Admin API
export const adminAPI = {
  getDashboardStats: (params?: any) =>
    api.get<ApiResponse<{ stats: any; recent: any; period: any }>>('/admin/dashboard/stats', {
      params,
    }),
  
  getSystemHealth: () =>
    api.get<ApiResponse<any>>('/admin/system/health'),
  
  getAnalytics: (params?: any) =>
    api.get<ApiResponse<{ type: string; period: string; data: any[]; dateRange: any }>>('/admin/analytics', {
      params,
    }),
  
  getActivity: (params?: any) =>
    api.get<ApiResponse<{ activities: any[]; count: number }>>('/admin/activity', { params }),
  
  exportData: (type: string, params?: any) =>
    api.get<ApiResponse<any>>(`/admin/export/${type}`, { params }),
  
  // API Health endpoints
  getAPIHealth: () =>
    api.get<ApiResponse<any>>('/admin/health/status'),
  
  forceAPIHealthCheck: () =>
    api.post<ApiResponse<any>>('/admin/health/check'),
  
  getProviderRecommendation: (preferredProvider?: string) =>
    api.get<ApiResponse<any>>('/admin/health/recommendation', { params: { preferredProvider } }),
  
  // Admin Wallet endpoints
  searchUsers: (query: string) =>
    api.get<ApiResponse<UserSearchResult[]>>(`/admin/wallet/search-users?q=${encodeURIComponent(query)}`),
  
  performManualTopup: (data: { userEmail: string; amount: number; reason: string; reference?: string }) =>
    api.post<ApiResponse<{ transaction: any; user: any; receipt?: any }>>('/admin/wallet/manual-topup', data),
  
  performManualDeduction: (data: { userEmail: string; amount: number; reason: string; reference?: string }) =>
    api.post<ApiResponse<{ transaction: any; user: any; receipt?: any }>>('/admin/wallet/manual-deduction', data),

  getPendingCommissions: (params?: { page?: number; limit?: number; status?: string }) =>
    api.get<ApiResponse<any>>('/admin/wallet/pending-commissions', { params }),

  approveCommission: (data: { commissionId: string; approvalNotes?: string }) =>
    api.post<ApiResponse<any>>('/admin/wallet/approve-commission', data),

  rejectCommission: (data: { commissionId: string; rejectionReason: string }) =>
    api.post<ApiResponse<any>>('/admin/wallet/reject-commission', data),

  // Loyalty Points - Redemption Approvals
  getLoyaltyRedemptions: (params?: { status?: string; limit?: number }) =>
    api.get<ApiResponse<any>>('/loyalty-points/admin/redemptions', { params }),

  approveLoyaltyRedemption: (id: string) =>
    api.post<ApiResponse<any>>(`/loyalty-points/admin/redemptions/${id}/approve`),

  rejectLoyaltyRedemption: (id: string, reason?: string) =>
    api.post<ApiResponse<any>>(`/loyalty-points/admin/redemptions/${id}/reject`, { reason }),

  // Wallet analytics endpoints
  getTopBalances: (limit: number = 5) =>
    api.get<ApiResponse<any>>(`/wallets/admin/top-balances?limit=${limit}`),

  getInactiveRecipients: (days: number = 7) =>
    api.get<ApiResponse<any>>(`/wallets/admin/inactive-recipients?days=${days}`),

  getAvgDeliveryTime: () =>
    api.get<ApiResponse<any>>('/wallets/admin/avg-delivery-time'),

  getAgentProfitBreakdown: (period: string = 'all', limit: number = 20) =>
    api.get<ApiResponse<any>>(`/commissions/agent-profit-breakdown?period=${period}&limit=${limit}`),

  // Backfill diagnostics
  getBackfillDiagnostics: () =>
    api.get<ApiResponse<any>>('/admin/backfill-supplier-cost-diagnostics'),

  runBackfillBatch: (dryRun: boolean = true, batchSize: number = 500) =>
    api.post<ApiResponse<any>>('/admin/backfill-supplier-cost-batch', { dryRun, batchSize }),
};

export const adminApprovalAPI = {
  getPending: (params?: { type?: 'admin' | 'agent' | 'all' }) =>
    api.get<ApiResponse<any>>('/admin-approval/pending', {
      params: params?.type && params.type !== 'all' ? { type: params.type } : undefined
    }),

  approve: (userId: string, data?: { reason?: string }) =>
    api.post<ApiResponse<any>>(`/admin-approval/approve/${userId}`, data || {}),

  reject: (userId: string, data: { reason: string }) =>
    api.post<ApiResponse<any>>(`/admin-approval/reject/${userId}`, data),

  getHistory: (params?: { type?: 'admin' | 'agent' | 'all' }) =>
    api.get<ApiResponse<any>>('/admin-approval/history', {
      params: params?.type && params.type !== 'all' ? { type: params.type } : undefined
    }),

  getStatus: (userId: string) =>
    api.get<ApiResponse<any>>(`/admin-approval/status/${userId}`),
};

// Agent Payment API
export const agentPaymentAPI = {
  getConfig: () =>
    api.get<ApiResponse<{ enabled: boolean; amount: number; currency: string; description: string }>>('/agent-payment/config'),
  
  updateConfig: (config: { enabled?: boolean; amount?: number; currency?: string; description?: string }) =>
    api.put<ApiResponse<any>>('/agent-payment/config', config),
  
  initiatePayment: (data: { paymentMethod: string; email?: string; phoneNumber?: string; network?: string }) =>
    api.post<ApiResponse<any>>('/agent-payment/initiate', data),
  
  verifyPayment: (reference: string, gateway?: string) =>
    api.post<ApiResponse<any>>('/agent-payment/verify', { reference, gateway }),
  
  getStatus: () =>
    api.get<ApiResponse<any>>('/agent-payment/status'),
  
  getPendingPayments: () =>
    api.get<ApiResponse<{ payments: any[]; count: number }>>('/agent-payment/pending'),
};

// Utility functions
export const setAuthToken = (token: string) => {
  void token;
};

export const removeAuthToken = () => {
};

// Supplier Management API
export const supplierAPI = {
  getConfig: () =>
    api.get<ApiResponse<{ activeSupplier: string; suppliers: any }>>('/admin/suppliers/config'),
  
  // Get real-time supplier balances from GHINTERNET and Hubnet APIs
  getStatus: () =>
    api.get<ApiResponse<{ 
      activeSupplier: string; 
      suppliers: {
        hubnet: {
          name: string;
          active: boolean;
          online: boolean;
          balance: number | null;
          currency: string;
          configured: boolean;
          lastChecked: string;
        };
        ghinternet: {
          name: string;
          active: boolean;
          online: boolean;
          reachable?: boolean;
          balance: number | null;
          currency: string;
          configured: boolean;
          username?: string;
          error?: string | null;
          errorCode?: string | null;
          lastChecked: string;
        };
        spfastit?: {
          name: string;
          active: boolean;
          online: boolean;
          balance: number | null;
          currency: string;
          configured?: boolean;
          error?: string | null;
          errorCode?: string | null;
          lastChecked: string;
        };
        mobigh?: {
          name: string;
          active: boolean;
          online: boolean;
          reachable?: boolean;
          balance: number | null;
          currency: string;
          configured: boolean;
          error?: string | null;
          errorCode?: string | null;
          lastChecked: string;
        };
      };
    }>>('/admin/suppliers/status'),
  
  setActiveSupplier: (supplier: 'Hubnet' | 'GHINTERNET' | 'Mobigh' | 'SPFastIT') =>
    api.put<ApiResponse<{ activeSupplier: string }>>('/admin/suppliers/active', { supplier }),
  
  setGhinternetApiKey: (apiKey: string) =>
    api.put<ApiResponse<{ apiKeyMasked: string }>>('/admin/suppliers/ghinternet/api-key', { apiKey }),

  setMobighApiKey: (apiKey: string) =>
    api.put<ApiResponse<{ apiKeyMasked: string }>>('/admin/suppliers/mobigh/api-key', { apiKey }),

  setSpfastitApiKey: (payload: { apiKey: string; apiBaseUrl?: string }) =>
    api.put<ApiResponse<{ apiKeyMasked: string; apiBaseUrl?: string }>>('/admin/suppliers/spfastit/api-key', payload),
  
  testSupplier: (supplier: string) =>
    api.post<ApiResponse<any>>(`/admin/suppliers/${supplier}/test`),
  
  getGhinternetPricing: () =>
    api.get<ApiResponse<any>>('/admin/suppliers/ghinternet/pricing'),
  
  // Withdrawals API
  withdrawals: {
    getWithdrawalData: () =>
      api.get<ApiResponse<any>>('/withdrawals/data'),
    
    submitWithdrawal: (data: any) =>
      api.post<ApiResponse<any>>('/withdrawals/submit', data),
    
    getWithdrawalStats: () =>
      api.get<ApiResponse<any>>('/withdrawals/stats'),
    
    getAdminWithdrawals: (params?: any) =>
      api.get<ApiResponse<any>>('/withdrawals/admin/all', { params }),
    
    processWithdrawal: (id: string, data: any) =>
      api.post<ApiResponse<any>>(`/withdrawals/admin/process/${id}`, data),

    getAdminPayoutAccounts: (params?: any) =>
      api.get<ApiResponse<{ accounts: any[]; pagination: any }>>('/withdrawals/admin/payout-accounts', { params }),

    approvePayoutAccount: (idOrAcc: any) => {
      const raw = typeof idOrAcc === 'object' && idOrAcc !== null ? (idOrAcc._id || idOrAcc.id || idOrAcc.$oid || idOrAcc) : idOrAcc;
      const cleanId = typeof raw === 'object' && raw !== null ? (raw.$oid || raw.toString?.() || '') : String(raw || '');
      return api.post<ApiResponse<any>>(`/withdrawals/admin/payout-accounts/${encodeURIComponent(cleanId)}/approve`);
    },

    rejectPayoutAccount: (idOrAcc: any, reason?: string) => {
      const raw = typeof idOrAcc === 'object' && idOrAcc !== null ? (idOrAcc._id || idOrAcc.id || idOrAcc.$oid || idOrAcc) : idOrAcc;
      const cleanId = typeof raw === 'object' && raw !== null ? (raw.$oid || raw.toString?.() || '') : String(raw || '');
      return api.post<ApiResponse<any>>(`/withdrawals/admin/payout-accounts/${encodeURIComponent(cleanId)}/reject`, { reason });
    },
  },
};

// Supplier Routing (Dynamic) API
export const supplierRoutingAPI = {
  getState: () =>
    api.get<ApiResponse<{ suppliers: any[]; routingRules: any[] }>>('/admin/routing/state'),

  getPlan: (network: string) =>
    api.get<ApiResponse<any>>(`/admin/routing/plan/${network}`),

  updateRule: (network: string, payload: { strategy?: 'priority' | 'cheapest' | 'manual'; manualSupplierSlug?: string | null }) =>
    api.put<ApiResponse<any>>(`/admin/routing/rules/${network}`, payload),

  upsertSetting: (payload: {
    supplierSlug: string;
    network: string;
    status?: 'on' | 'off';
    priority?: number;
    price?: number | null;
    is_fallback?: boolean;
  }) => api.put<ApiResponse<any>>('/admin/routing/settings', payload),

  setSupplierStatus: (slug: string, payload: { status: 'active' | 'inactive' }) =>
    api.put<ApiResponse<any>>(`/admin/routing/suppliers/${slug}/status`, payload),

  getLogs: (params?: { network?: string; success?: boolean; limit?: number; offset?: number }) =>
    api.get<ApiResponse<{ logs: any[]; total: number; limit: number; offset: number }>>('/admin/routing/logs', { params }),

  getMetrics: (params?: { network?: string; days?: number }) =>
    api.get<ApiResponse<any>>('/admin/routing/metrics', { params })
};

// Withdrawals API
export const withdrawalsAPI = {
  getWithdrawalData: () =>
    api.get<ApiResponse<any>>('/withdrawals/data'),
  
  submitWithdrawal: (data: any) =>
    api.post<ApiResponse<any>>('/withdrawals/submit', data),
  
  getWithdrawalStats: () =>
    api.get<ApiResponse<any>>('/withdrawals/stats'),
  
  getAdminWithdrawals: (params?: any) =>
    api.get<ApiResponse<any>>('/withdrawals/admin/all', { params }),
  
  processWithdrawal: (id: string, data: any) =>
    api.post<ApiResponse<any>>(`/withdrawals/admin/process/${id}`, data),

  listBanks: (type: string = 'mobile_money') =>
    api.get<ApiResponse<any>>(`/withdrawals/paystack/banks?type=${type}`),

  resolveAccount: (accountNumber: string, bankCode: string) =>
    api.get<ApiResponse<any>>(`/withdrawals/paystack/resolve-account?account_number=${accountNumber}&bank_code=${bankCode}`),

  getAdminPayoutAccounts: (params?: any) =>
    api.get<ApiResponse<{ accounts: any[]; pagination: any }>>('/withdrawals/admin/payout-accounts', { params }),

  approvePayoutAccount: (idOrAcc: any) => {
    const raw = typeof idOrAcc === 'object' && idOrAcc !== null ? (idOrAcc._id || idOrAcc.id || idOrAcc.$oid || idOrAcc) : idOrAcc;
    const cleanId = typeof raw === 'object' && raw !== null ? (raw.$oid || raw.toString?.() || '') : String(raw || '');
    return api.post<ApiResponse<any>>(`/withdrawals/admin/payout-accounts/${encodeURIComponent(cleanId)}/approve`);
  },

  rejectPayoutAccount: (idOrAcc: any, reason?: string) => {
    const raw = typeof idOrAcc === 'object' && idOrAcc !== null ? (idOrAcc._id || idOrAcc.id || idOrAcc.$oid || idOrAcc) : idOrAcc;
    const cleanId = typeof raw === 'object' && raw !== null ? (raw.$oid || raw.toString?.() || '') : String(raw || '');
    return api.post<ApiResponse<any>>(`/withdrawals/admin/payout-accounts/${encodeURIComponent(cleanId)}/reject`, { reason });
  },
};

// Attach withdrawals to main API object for backward compatibility
(api as any).withdrawals = withdrawalsAPI;

// Beneficiaries API
export interface Beneficiary {
  _id: string;
  nickname: string;
  phone: string;
  network: 'mtn' | 'airteltigo' | 'telecel';
  lastUsed: string | null;
  createdAt: string;
}

export const beneficiariesAPI = {
  getAll: () =>
    api.get<{ success: boolean; beneficiaries: Beneficiary[] }>('/beneficiaries'),

  create: (data: { nickname: string; phone: string; network: string }) =>
    api.post<{ success: boolean; beneficiary: Beneficiary }>('/beneficiaries', data),

  update: (id: string, data: { nickname?: string; phone?: string; network?: string }) =>
    api.put<{ success: boolean; beneficiary: Beneficiary }>(`/beneficiaries/${id}`, data),

  remove: (id: string) =>
    api.delete<{ success: boolean }>(`/beneficiaries/${id}`),

  markUsed: (id: string) =>
    api.patch<{ success: boolean }>(`/beneficiaries/${id}/use`),
};

// Loyalty Points API
export interface LoyaltyPointsSummary {
  availablePoints: number;
  estimatedWalletCredit: number;
  pendingRedemptionPoints: number;
  pendingRedemptionCredit: number;
  pointsToGhsRate: number;
}

export interface LoyaltyRedemption {
  _id: string;
  points: number;
  creditAmount: number;
  status: 'pending' | 'approved' | 'rejected';
  rejectionReason?: string;
  createdAt: string;
  reviewedAt?: string;
  walletTransactionId?: string;
}

export const loyaltyPointsAPI = {
  getSummary: () =>
    api.get<ApiResponse<LoyaltyPointsSummary>>('/loyalty-points/summary'),

  getRedemptions: (limit = 20) =>
    api.get<ApiResponse<LoyaltyRedemption[]>>(
      `/loyalty-points/my-redemptions?limit=${limit}`
    ),

  redeem: (points: number) =>
    api.post<ApiResponse<{ request: LoyaltyRedemption }>>(
      '/loyalty-points/redeem',
      { points }
    ),
};

// Error handling utility
export const handleApiError = (error: any): string => {
  if (error.response?.data?.message) {
    return error.response.data.message;
  } else if (error.message) {
    return error.message;
  } else {
    return 'An unexpected error occurred';
  }
};

// Request cancellation utility
export const createCancelToken = () => {
  return axios.CancelToken.source();
};

export { api, API_BASE_URL };
